import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Grid,
  Divider
} from '@mui/material';
import { insiderApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

function StrategyControls() {
  const { isAdmin } = useAuth();
  const canEdit = isAdmin();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await insiderApi.getStrategyConfig();
      setConfig(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setSuccess(false);
    setError(null);

    try {
      const response = await insiderApi.updateStrategyConfig({
        enabled: config.enabled,
        max_position_size_pct: parseFloat(config.max_position_size_pct),
        kelly_fraction: parseFloat(config.kelly_fraction),
        min_confidence_strong_buy: parseFloat(config.min_confidence_strong_buy),
        min_confidence_buy: parseFloat(config.min_confidence_buy)
      });
      setConfig(response.data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig({ ...config, [field]: value });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">Failed to load strategy configuration</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Strategy Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Adjust trading strategy parameters. Changes take effect immediately.
      </Typography>

      {!canEdit && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Viewing mode only. Contact administrator to make changes.
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Configuration updated successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error: {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled || false}
                  onChange={(e) => handleChange('enabled', e.target.checked)}
                  color={config.enabled ? 'success' : 'default'}
                  disabled={!canEdit}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Trading Enabled
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Master kill switch for automated trading
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Position Sizing
          </Typography>
          <TextField
            fullWidth
            label="Max Position Size (%)"
            type="number"
            value={config.max_position_size_pct ? (config.max_position_size_pct * 100).toFixed(2) : ''}
            onChange={(e) => handleChange('max_position_size_pct', parseFloat(e.target.value) / 100)}
            inputProps={{ min: 1, max: 20, step: 0.5 }}
            helperText="Maximum % of portfolio per position (1-20%)"
            sx={{ mb: 2 }}
            disabled={!canEdit}
          />
          <TextField
            fullWidth
            label="Kelly Fraction (%)"
            type="number"
            value={config.kelly_fraction ? (config.kelly_fraction * 100).toFixed(2) : ''}
            onChange={(e) => handleChange('kelly_fraction', parseFloat(e.target.value) / 100)}
            inputProps={{ min: 10, max: 100, step: 5 }}
            helperText="Kelly Criterion fraction (10-100%). Lower = more conservative"
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Signal Thresholds
          </Typography>
          <TextField
            fullWidth
            label="Min Confidence - STRONG_BUY (%)"
            type="number"
            value={config.min_confidence_strong_buy ? (config.min_confidence_strong_buy * 100).toFixed(2) : ''}
            onChange={(e) => handleChange('min_confidence_strong_buy', parseFloat(e.target.value) / 100)}
            inputProps={{ min: 50, max: 100, step: 1 }}
            helperText="Minimum ML confidence for STRONG_BUY (50-100%)"
            sx={{ mb: 2 }}
            disabled={!canEdit}
          />
          <TextField
            fullWidth
            label="Min Confidence - BUY (%)"
            type="number"
            value={config.min_confidence_buy ? (config.min_confidence_buy * 100).toFixed(2) : ''}
            onChange={(e) => handleChange('min_confidence_buy', parseFloat(e.target.value) / 100)}
            inputProps={{ min: 50, max: 100, step: 1 }}
            helperText="Minimum ML confidence for BUY (50-100%)"
            disabled={!canEdit}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Exit Rules (Read-Only)
          </Typography>
          <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" paragraph>
              <strong>Hard Stop Loss:</strong> {config.hard_stop_loss ? (config.hard_stop_loss * 100).toFixed(0) : '-'}% (closes 100% of position)
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Time-Adjusted Stops:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ ml: 2 }}>
              {'\u2022'} Days 1-7: {config.stop_loss_days_1_7 ? (config.stop_loss_days_1_7 * 100).toFixed(0) : '-'}%<br />
              {'\u2022'} Days 8-30: {config.stop_loss_days_8_30 ? (config.stop_loss_days_8_30 * 100).toFixed(0) : '-'}%<br />
              {'\u2022'} Days 31-60: {config.stop_loss_days_31_60 ? (config.stop_loss_days_31_60 * 100).toFixed(0) : '-'}%
            </Typography>
            <Typography variant="body2" paragraph sx={{ mt: 2 }}>
              <strong>Max Hold Time:</strong> {config.max_hold_days || '-'} days
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Profit Targets:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ ml: 2 }}>
              {'\u2022'} {config.profit_target_1_return ? (config.profit_target_1_return * 100).toFixed(0) : '-'}% in &lt; {config.profit_target_1_days || '-'} days → Sell {config.profit_target_1_sell_pct ? (config.profit_target_1_sell_pct * 100).toFixed(0) : '-'}%<br />
              {'\u2022'} {config.profit_target_1_return ? (config.profit_target_1_return * 100).toFixed(0) : '-'}% in {(config.profit_target_1_days || 0) + 1}-{config.max_hold_days || '-'} days → Sell 33%<br />
              {'\u2022'} {config.profit_target_2_return ? (config.profit_target_2_return * 100).toFixed(0) : '-'}% anytime → Sell {config.profit_target_2_sell_pct ? (config.profit_target_2_sell_pct * 100).toFixed(0) : '-'}%<br />
              {'\u2022'} {config.profit_target_3_return ? (config.profit_target_3_return * 100).toFixed(0) : '-'}% anytime → Sell {config.profit_target_3_sell_pct ? (config.profit_target_3_sell_pct * 100).toFixed(0) : '-'}%
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Exit rules can only be modified via BigQuery database
            </Typography>
          </Box>
        </Grid>

        {canEdit && (
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSave}
              disabled={saving}
              fullWidth
            >
              {saving ? <CircularProgress size={24} /> : 'Save Configuration'}
            </Button>
          </Grid>
        )}

        <Grid item xs={12}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Event-Driven System:</strong> Changes take effect immediately. The strategy engine processes new insider filings in real-time (~51 seconds from filing to trade execution during market hours).
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default StrategyControls;
